import React from 'react';
import {
	Box,
	FormControlLabel,
	Radio,
	RadioGroup,
	Typography,
} from '@mui/material';
import { useGlobalization } from '@Translations/useGlobalization';
import { LabelInfoTip } from './LabelInfoTip';
import { CombineMethod } from './riskSetupDraft.types';

export interface CombineMethodRadiosProps {
	riskMethod: CombineMethod | undefined;
	disabled: boolean;
	onChange: (method: CombineMethod) => void;
}

interface MethodOption {
	value: CombineMethod;
	name: string;
	formula: string;
	help: string;
	badge?: string;
}

const MethodLabel = ({ value, name, formula, help, badge }: MethodOption) => (
	<Box sx={{ pt: '2px' }}>
		<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
			<Typography
				sx={{ fontSize: 15, fontWeight: 700, color: '#1b2733' }}>
				{name}
			</Typography>
			{badge && (
				<Box
					component="span"
					sx={{
						px: 0.75,
						py: '1px',
						borderRadius: '9999px',
						fontSize: 11,
						fontWeight: 700,
						color: '#1b6fb3',
						background: '#e6f0fb',
					}}>
					{badge}
				</Box>
			)}
			<LabelInfoTip text={help} dataCy={`combine-method-${value}-help`} />
		</Box>
		<Typography sx={{ fontSize: 13, color: '#5b6472', mt: 0.25 }}>
			{formula}
		</Typography>
	</Box>
);

export const CombineMethodRadios = ({
	riskMethod,
	disabled,
	onChange,
}: CombineMethodRadiosProps) => {
	const { t } = useGlobalization();
	const options: MethodOption[] = [
		{
			value: 'multiplied',
			name: t('Multiply'),
			formula: t('Risk = LoF × CoF'),
			badge: t('Most common'),
			help: t(
				'A pipe scores high only if it is both likely to fail and in a high-consequence spot — the classic view most utilities use. Example: very likely to fail but in an empty field (LoF 9, CoF 1) → 9 × 1 = 9… on a 0–100 scale that is low.',
			),
		},
		{
			value: 'added',
			name: t('Add'),
			formula: t('Risk = LoF + CoF'),
			help: t(
				'A pipe scores high if either side is high. Same pipe (LoF 9, CoF 1) → 9 + 1 = 10, still high.',
			),
		},
	];
	return (
		<RadioGroup
			row
			// '' keeps the group controlled with nothing checked for old models.
			value={riskMethod ?? ''}
			onChange={(_event, value) => onChange(value as CombineMethod)}
			sx={{ gap: 5, flexWrap: 'wrap' }}>
			{options.map(option => (
				<FormControlLabel
					key={option.value}
					disabled={disabled}
					value={option.value}
					sx={{ alignItems: 'flex-start', m: 0, maxWidth: 340 }}
					control={
						<Radio
							sx={{
								pt: 0,
								color: '#b3bac4',
								'&.Mui-checked': { color: '#3b8fd4' },
							}}
							inputProps={
								{
									'data-cy': `combine-method-${option.value}`,
								} as React.InputHTMLAttributes<HTMLInputElement>
							}
						/>
					}
					label={<MethodLabel {...option} />}
				/>
			))}
		</RadioGroup>
	);
};
